import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import adminService from "../../api/services/adminService";

function toDatetimeLocalValue(isoOrDate) {
  if (!isoOrDate) return "";
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const STATUS_OPTIONS = [
  "INITIAL_PURCHASE",
  "RENEWAL",
  "CANCELLATION",
  "SUBSCRIPTION_EXTENDED",
  "PRODUCT_CHANGE",
  "MANUAL",
  "BILLING_ISSUE",
  "EXPIRATION",
  "UNCANCELLATION",
];

const STATUS_LABELS = {
  INITIAL_PURCHASE: "Initial Purchase",
  RENEWAL: "Renewal",
  CANCELLATION: "Cancellation",
  SUBSCRIPTION_EXTENDED: "Subscription extended",
  PRODUCT_CHANGE: "Product change",
  MANUAL: "Manual",
  BILLING_ISSUE: "Billing issue",
  EXPIRATION: "Expiration",
  UNCANCELLATION: "Uncancellation",
};

/** Currencies seen in app / admin stats; row value merged in if missing. */
const BASE_CURRENCY_OPTIONS = ["PKR", "AED", "INR", "SAR", "USD", "EUR", "GBP"];

const PAYMENT_METHOD_OPTIONS = ["", "MANUAL_TRANSFER", "APP_STORE", "PLAY_STORE", "BANK_ALFALAH"];

function mergeCurrencyOptions(current) {
  const c = current != null ? String(current).trim().toUpperCase() : "";
  const list = [...BASE_CURRENCY_OPTIONS];
  if (c && !list.includes(c)) list.unshift(c);
  return list;
}

function mergeStatusOptions(current) {
  const s = current != null ? String(current).trim() : "";
  const list = [...STATUS_OPTIONS];
  if (s && !list.includes(s)) list.unshift(s);
  return list;
}

export default function SubscriptionEditModal({ open, item, plans = [], onClose, onSaved }) {
  const [form, setForm] = useState({
    subscription: "",
    start_date: "",
    end_date: "",
    amount: "",
    currency: "",
    status: "",
    payment_method: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setForm({
      subscription: String(item.subscription ?? ""),
      start_date: toDatetimeLocalValue(item.start_date),
      end_date: toDatetimeLocalValue(item.end_date),
      amount: item.amount != null ? String(item.amount) : "",
      currency: (item.currency || "PKR").toString().toUpperCase(),
      status: item.status ? String(item.status) : "",
      payment_method: item.payment_method ? String(item.payment_method) : "",
    });
  }, [open, item]);

  if (!open || !item) return null;

  const currencySelectOptions = mergeCurrencyOptions(form.currency || item.currency);
  const statusSelectOptions = mergeStatusOptions(form.status || item?.status);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subscription) {
      toast.error("Select a subscription plan");
      return;
    }
    if (!form.start_date || !form.end_date) {
      toast.error("Start and end dates are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        subscription: parseInt(form.subscription, 10),
        start_date: form.start_date,
        end_date: form.end_date,
        amount: form.amount === "" ? 0 : Number(form.amount),
        currency: form.currency.trim() || "PKR",
        status: form.status,
        payment_method: form.payment_method,
      };
      const res = await adminService.updateSubscriptionRecord(item.id, payload);
      if (res?.success) {
        toast.success(res?.message || "Subscription updated");
        onSaved?.();
        onClose();
      } else {
        toast.error(res?.message || "Update failed");
      }
    } catch (err) {
      toast.error(err?.message || err?.error || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}>
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-200"
        onMouseDown={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Edit subscription record</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 p-1 rounded-lg hover:bg-gray-100"
            aria-label="Close">
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Record ID <span className="font-mono font-medium">{item.id}</span> — User{" "}
            <span className="font-mono">{item.user_id}</span> ({item.email})
          </p>
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            Developer-only. Changes sync the user&apos;s premium flag from remaining active subscriptions.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
            <select
              name="subscription"
              value={form.subscription}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              required>
              <option value="">Select plan</option>
              {(plans || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name || `Plan ${p.id}`}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start</label>
              <input
                type="datetime-local"
                name="start_date"
                value={form.start_date}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-red-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End</label>
              <input
                type="datetime-local"
                name="end_date"
                value={form.end_date}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-red-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
              <input
                type="number"
                name="amount"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                name="currency"
                value={form.currency ? String(form.currency).toUpperCase() : "PKR"}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-red-500">
                {currencySelectOptions.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status (RevenueCat / manual)</label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-red-500">
              <option value="">(Clear / none)</option>
              {statusSelectOptions.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s] ? `${STATUS_LABELS[s]} (${s})` : s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment method</label>
            <select
              name="payment_method"
              value={form.payment_method}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-red-500">
              {PAYMENT_METHOD_OPTIONS.map((pm) => (
                <option key={pm || "none"} value={pm}>
                  {pm || "(clear)"}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
